import { useTranslation } from 'react-i18next';
import type { AllocationStatus, DashboardTotals } from '../types';
import { ALLOCATION_STATUS_COLOR } from './launch-unit-status';

interface LaunchStatusDonutProps {
  totals: DashboardTotals;
}

const SEGMENT_KEYS: AllocationStatus[] = [
  'available', 'held', 'deposit_pending', 'reserved_with_deposit', 'sold',
];

/**
 * SVG donut chart showing unit allocation distribution by status.
 */
export function LaunchStatusDonut({ totals }: LaunchStatusDonutProps) {
  const { t } = useTranslation('features/launches');
  const values = SEGMENT_KEYS.map((key) => totals[key] ?? 0);
  const total = values.reduce((a, b) => a + b, 0);
  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-[180px]">
        <span className="text-[length:var(--text-small)] text-[var(--text-tertiary)]">{t('dashboard.noData')}</span>
      </div>
    );
  }

  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex flex-col items-center gap-[var(--space-4)]">
      <svg viewBox="0 0 200 200" width={180} height={180} role="img" aria-label={t('dashboard.statusDistribution')}>
        {SEGMENT_KEYS.map((key, i) => {
          const pct = values[i] / total;
          const dash = pct * circumference;
          const gap = circumference - dash;
          const el = (
            <circle
              key={key}
              cx={100}
              cy={100}
              r={radius}
              fill="none"
              stroke={ALLOCATION_STATUS_COLOR[key]}
              strokeWidth={24}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
              transform="rotate(-90 100 100)"
            />
          );
          offset += dash;
          return el;
        })}
        <text x={100} y={97} textAnchor="middle" className="text-[length:var(--text-page-title)] font-semibold fill-[var(--text-primary)]">
          {Math.round((totals.sold / total) * 100)}%
        </text>
        <text x={100} y={118} textAnchor="middle" className="text-[10px] fill-[var(--text-tertiary)]">
          {t('dashboard.soldLabel')}
        </text>
      </svg>

      <div className="flex flex-wrap justify-center gap-[var(--space-3)]">
        {SEGMENT_KEYS.map((key, i) => (
          <div key={key} className="flex items-center gap-[var(--space-1)]">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ALLOCATION_STATUS_COLOR[key] }} />
            <span className="text-[length:var(--text-meta)] text-[var(--text-secondary)]">
              {t(`status.${key}`)} <span className="tabular-nums">({values[i]})</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
